/* eslint-disable @typescript-eslint/no-unused-vars */
import { SwitchWithText } from '@adotel/components'
import { useLazyQuery, useQuery } from '@apollo/client'
import { Hidden, Collapse, useMediaQuery, useTheme } from '@material-ui/core'
import moment from 'moment-jalaali'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import PageHeader from '../../components/PageHeader'
import { CRS_INVENTORY_QUERY } from '../../graphql/Inventory'
import { CHANNELS_QUERY } from '../../graphql/channel'
import { addDaysToDate, DateObject, getTodayDate } from '../../helpers/date'
import {
  getDefaultCurrency,
  getEntityCode,
  getEntityId,
  getEntityName,
  getRoleFromStorage
} from '../../helpers/userData'
import { getTranslate } from '../../localization'
import {
  generateInventoryMockData,
  SingleRoomTypeData
} from '../../mockData/inventory'
import inventoryPagesRoleManagement from '../../staticRoleManagement/inventoryRoleManagement'
import { ROLE } from '../../staticRoleManagement/types'
import { Channel } from '../../types/channel'
import { Currency } from '../../types/currency'
import {
  CRSInventoryData,
  ChannelInventoryData,
  GetInventoryInput,
  RoomTypeForInventory
} from '../../types/inventory'
import InventoryBasicFilters from './InventoryBasicFilters'
import InventoryDateRow from './InventoryDateRow'
import InventoryMobile from './InventoryMobile'
import InventoryRoomtypeBox from './InventoryRoomtypeBox'
import InventorySecondaryFiltersBox from './InventorySecondaryFiltersBox'
import InventorySkeleton from './InventorySkeleton.skeleton'
import InventoryTableOptions from './InventoryTableOptions'
import useStyles from './styles/index.styles'

const inventoryOneYearMockData = generateInventoryMockData()

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type ChangeInventoryDataType = {
  entityType: 'roomType' | 'ratePlan' | 'channel'
  entityValue: string
  fieldType:
    | 'availability'
    | 'price'
    | 'closed'
    | 'CTA'
    | 'CTD'
    | 'minStay'
    | 'maxStay'
    | 'extraBedPrice'
    | 'allocatedAvail'
  data: {
    date: string
    value: number | boolean
  }[]
  metaData?: {
    roomType: string
    ratePlan: string
  }
}
type RangeType = {
  label: string
  value: 'TWO_WEEKS' | 'MONTH'
}

const RANGE_ITEMS: RangeType[] = [
  {
    label: getTranslate('View two weeks'),
    value: 'TWO_WEEKS'
  },
  {
    label: getTranslate('View monthly'),
    value: 'MONTH'
  }
]

export const Inventory = () => {
  const classes = useStyles()
  const [selectedRange, setselectedRange] = useState(0)
  const [hideZeroes, sethideZeroes] = useState<boolean>(false)
  const theme = useTheme()

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    if (RANGE_ITEMS[selectedRange].value !== 'TWO_WEEKS') {
      sethideZeroes(true)
    }
  }, [selectedRange])

  const [secondaryFilterIsOpen, setsecondaryFilterIsOpen] = useState(false)
  const isRoleAccommodation = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.IS_ACCOMMODATION.rolesListWithReadAccess?.includes(
      getRoleFromStorage()
    )
  const [autoCompleteLabelAccommodation, setautoCompleteLabelAccommodation] =
    useState<any>({
      id: isRoleAccommodation ? getEntityId() : '',
      code: isRoleAccommodation ? getEntityCode() : '',
      label: isRoleAccommodation ? getEntityName() : '',
      value: isRoleAccommodation ? getEntityId() : ''
    })

  const [selectedDateInDatePicker, setselectedDateInDatePicker] =
    useState<DateObject>({
      gregorianStartDate: getTodayDate(),
      gregorianEndDate: addDaysToDate(getTodayDate(), 13)
    })

  const firstDayIndexInInventory = useMemo(
    () =>
      moment(selectedDateInDatePicker.gregorianStartDate, 'YYYY-MM-DD').diff(
        moment().startOf('day'),
        'days'
      ),
    [selectedDateInDatePicker]
  )

  const { data: channelData, loading: loadingForChannel } = useQuery<{
    channels: Channel[]
  }>(CHANNELS_QUERY)
  // based on momentjs document monday is 1, so:
  // thursday = 4
  // friday = 5
  const selectedDayInDatePickerWeekIndex = useMemo(
    () => moment(selectedDateInDatePicker.gregorianStartDate).day(),
    [selectedDateInDatePicker]
  )

  const weekendIndexes = useMemo(
    () => [
      selectedDayInDatePickerWeekIndex <= 4
        ? 4 - selectedDayInDatePickerWeekIndex
        : 11 - selectedDayInDatePickerWeekIndex,
      selectedDayInDatePickerWeekIndex <= 5
        ? 5 - selectedDayInDatePickerWeekIndex
        : 12 - selectedDayInDatePickerWeekIndex
    ],
    [selectedDateInDatePicker]
  )

  const isRoleChannel = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.IS_CHANNEL.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )
  const isRoleProvider = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.IS_PROVIDER.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  const [crsInventoryData, setcrsInventoryData] = useState<CRSInventoryData>()
  const [getInventoryInput, setgetInventoryInput] = useState<GetInventoryInput>(
    {
      propertyCode: isRoleAccommodation ? getEntityCode() || '' : '',
      channelID: isRoleChannel ? getEntityId() || '' : '',
      Currency: getDefaultCurrency(),
      startDate: selectedDateInDatePicker.gregorianStartDate || '',
      endDate: selectedDateInDatePicker.gregorianEndDate || '',
      providerCode: isRoleProvider ? getEntityCode() || '' : '',
      ratePlanCode: '',
      roomTypeCode: ''
    }
  )

  useEffect(() => {
    if (!!selectedDateInDatePicker) {
      setgetInventoryInput({
        ...getInventoryInput,
        startDate: selectedDateInDatePicker.gregorianStartDate || '',
        endDate: selectedDateInDatePicker.gregorianEndDate || ''
      })
    }
  }, [selectedDateInDatePicker])

  const [getCRSInventory, { loading: loadingForCRSInventory }] = useLazyQuery<{
    crsInventory: CRSInventoryData
  }>(CRS_INVENTORY_QUERY, {
    onCompleted: ({ crsInventory }) => {
      setcrsInventoryData(crsInventory)
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only'
  })
  useEffect(() => {
    if (getInventoryInput.propertyCode && !isSmallScreen)
      getCRSInventory({
        variables: {
          input: getInventoryInput
        }
      })
  }, [
    getInventoryInput.propertyCode,
    getInventoryInput.startDate,
    getInventoryInput.endDate,
    getInventoryInput.Currency,
    getEntityId()
  ])

  // const handleChangeRoomtypeRowData = (
  //   newRowData: SingleRoomTypeData,
  //   index: number
  // ) => {
  //   setnewInventoryData([
  //     ...newInventoryData.slice(0, index),
  //     newRowData,
  //     ...newInventoryData.slice(index + 1)
  //   ])
  // }

  const handleChangeRange = (newRangeIndex: number) => {
    setselectedRange(newRangeIndex)
    setselectedDateInDatePicker({
      gregorianStartDate: selectedDateInDatePicker.gregorianStartDate,
      gregorianEndDate: addDaysToDate(
        selectedDateInDatePicker.gregorianStartDate!,
        RANGE_ITEMS[newRangeIndex].value === 'TWO_WEEKS' ? 13 : 30
      )
    })
  }
  const [occupancy, setoccupancy] = useState<number | 'def'>('def')

  return (
    <div className={classes.root}>
      <PageHeader title={getTranslate('Inventory Table')} />
      <Hidden smDown>
        <SwitchWithText
          items={[RANGE_ITEMS[0].label, RANGE_ITEMS[1].label]}
          selectedItemIndex={selectedRange}
          onChange={handleChangeRange}
        />
        <InventoryBasicFilters
          {...{
            autoCompleteLabelAccommodation,
            setautoCompleteLabelAccommodation,
            getInventoryInput,
            setgetInventoryInput,
            setsecondaryFilterIsOpen,
            isRoleChannel,
            isRoleProvider
          }}
        />
        {autoCompleteLabelAccommodation &&
          autoCompleteLabelAccommodation.value && (
            <>
              <InventoryTableOptions
                occupancy={occupancy}
                isRangeMonth={RANGE_ITEMS[selectedRange].value !== 'TWO_WEEKS'}
                hideZeroes={hideZeroes}
                sethideZeroes={sethideZeroes}
                setOccupancy={setoccupancy}
                refreshInventory={() => {
                  if (getInventoryInput.propertyCode && !isSmallScreen)
                    getCRSInventory({
                      variables: {
                        input: getInventoryInput
                      }
                    })
                }}
                {...{ secondaryFilterIsOpen, setsecondaryFilterIsOpen }}
                getInventoryInput={getInventoryInput}
                setgetInventoryInput={setgetInventoryInput}
              />
              <Collapse in={secondaryFilterIsOpen}>
                {!!getInventoryInput.propertyCode && (
                  <div>
                    <InventorySecondaryFiltersBox
                      channels={channelData?.channels || []}
                      {...{
                        getInventoryInput,
                        setgetInventoryInput,
                        getCRSInventory,
                        loadingForCRSInventory,
                        loadingForChannel,
                        isRoleChannel,
                        isRoleProvider
                      }}
                      propertyCode={getInventoryInput.propertyCode}
                    />
                  </div>
                )}
              </Collapse>
              <InventoryDateRow
                {...{ selectedDateInDatePicker, setselectedDateInDatePicker }}
                range={RANGE_ITEMS[selectedRange].value}
              />
              {loadingForCRSInventory && <InventorySkeleton />}
              {!loadingForCRSInventory &&
                !!getInventoryInput.propertyCode &&
                crsInventoryData?.roomTypes.map(
                  (singleRoomType: RoomTypeForInventory, index) => (
                    <InventoryRoomtypeBox
                      key={index}
                      roomData={singleRoomType}
                      channels={channelData?.channels || []}
                      range={RANGE_ITEMS[selectedRange].value}
                      refreshInventory={() => {
                        if (getInventoryInput.propertyCode && !isSmallScreen)
                          getCRSInventory({
                            variables: {
                              input: getInventoryInput
                            }
                          })
                      }}
                      {...{
                        getInventoryInput,
                        hideZeroes,
                        occupancy,
                        weekendIndexes
                      }}
                      // onSaveRoomData={(newRowData) =>
                      //   handleChangeRoomtypeRowData(newRowData, index)
                      // }
                      // ChannelInventory={ChannelInventory}
                    />
                  )
                )}
            </>
          )}
      </Hidden>
      <Hidden mdUp>
        <InventoryMobile
          channels={channelData?.channels || []}
          {...{
            getInventoryInput,
            setgetInventoryInput,
            autoCompleteLabelAccommodation,
            setautoCompleteLabelAccommodation,
            getCRSInventory,
            crsInventoryData,
            hideZeroes,
            occupancy,
            sethideZeroes,
            weekendIndexes,
            loadingForCRSInventory,
            selectedDateInDatePicker,
            setselectedDateInDatePicker,
            setoccupancy,
            loadingForChannel,
            isRoleChannel,
            isRoleProvider
          }}
        />
      </Hidden>
    </div>
  )
}
